import { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

import { API_PROJECT_WORKSTREAM_TRACKING } from "../../../config/Endpoints.js";
import { getApi } from "../../utils/api.ts";
import type { ContributorTrackingReport, ProjectActivityCategory, ProjectActivityResponse, ProjectTrackingReport, TimeBucket, TrackableProject, WorkstreamAnalytics, WorkstreamTrackingReport } from "./types.ts";

function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === "object" && "data" in raw) return (raw as { data: T }).data;
  return raw as T;
}

export async function getWorkstreamAnalytics(
  projectId: number,
  workstreamId: number,
  bucket: TimeBucket,
  from?: string,
  to?: string,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ bucket });
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const token = Cookies.get("accessToken");
  const response = await getApi<unknown>(
    `${API_PROJECT_WORKSTREAM_TRACKING}/projects/${projectId}/report/workstreams/${workstreamId}/analytics?${params}`,
    token,
    "json",
    signal,
  );
  return unwrap<WorkstreamAnalytics>(response.data);
}

function populateWorkstreamFinancials(
  workstreams: WorkstreamTrackingReport[],
  contributors: ContributorTrackingReport[],
): WorkstreamTrackingReport[] {
  return workstreams.map((workstream) => {
    const members = contributors.filter((item) =>
      item.workstreamId === workstream.workstreamId || item.workstream === workstream.workstream,
    );
    if (!members.length) return workstream;

    const contributorCommercial = members.reduce((sum, item) => sum + Number(item.commercialValue ?? 0), 0);
    const contributorInternal = members.reduce((sum, item) => sum + Number(item.internalCost ?? 0), 0);
    const commercialBurn = workstream.commercialBurn ?? contributorCommercial;
    const internalCost = workstream.internalCost ?? contributorInternal;
    const forecastMargin = workstream.forecastMargin
      ?? (commercialBurn > 0 ? ((commercialBurn - internalCost) / commercialBurn) * 100 : null);

    return { ...workstream, commercialBurn, internalCost, forecastMargin };
  });
}

export function useTrackableProjects() {
  const [projects, setProjects] = useState<TrackableProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = Cookies.get("accessToken");

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true); setError(null);
    try {
      const response = await getApi<unknown>(`${API_PROJECT_WORKSTREAM_TRACKING}/projects/trackable`, token, "json", signal);
      if (!signal?.aborted) setProjects(unwrap<TrackableProject[]>(response.data) ?? []);
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!signal?.aborted) { setError(e instanceof Error ? e.message : "Unable to load trackable projects"); setProjects([]); }
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [token]);

  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);
  return { projects, loading, error, reload: load };
}

export function useWorkstreamTracking(
  projectId: string,
  includeShadow = false,
  workstreamId: number | null = null,
  includeResearch = false,
  includeGovernance = false,
  includeSalesCommission = true,
) {
  const [project, setProject] = useState<ProjectTrackingReport | null>(null);
  const [workstreams, setWorkstreams] = useState<WorkstreamTrackingReport[]>([]);
  const [contributors, setContributors] = useState<ContributorTrackingReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = Cookies.get("accessToken");

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!projectId) { setProject(null); setWorkstreams([]); setContributors([]); setError(null); return; }
    setLoading(true); setError(null);
    const base = `${API_PROJECT_WORKSTREAM_TRACKING}/projects/${projectId}/report`;
    const reportParams = new URLSearchParams({
      includeShadow: String(includeShadow),
      includeResearch: String(includeResearch),
      includeGovernance: String(includeGovernance),
      includeSalesCommission: String(includeSalesCommission),
    });
    if (workstreamId != null) reportParams.set("workstreamId", String(workstreamId));
    const reportQuery = `?${reportParams}`;
    try {
      const [projectResponse, workstreamResponse, contributorResponse, configurationResponse] = await Promise.all([
        getApi<unknown>(`${base}${reportQuery}`, token, "json", signal),
        getApi<unknown>(`${base}/workstreams`, token, "json", signal),
        getApi<unknown>(`${base}/contributors${reportQuery}`, token, "json", signal),
        getApi<unknown>(`${API_PROJECT_WORKSTREAM_TRACKING}/projects/${projectId}/configuration`, token, "json", signal),
      ]);
      if (!signal?.aborted) {
        const loadedContributors = unwrap<ContributorTrackingReport[]>(contributorResponse.data) ?? [];
        const loadedWorkstreams = unwrap<WorkstreamTrackingReport[]>(workstreamResponse.data) ?? [];
        const loadedProject = unwrap<ProjectTrackingReport>(projectResponse.data);
        const configuration = unwrap<Partial<ProjectTrackingReport> & { totalHours?: number | null }>(configurationResponse.data) ?? {};
        setProject({
          ...configuration,
          ...loadedProject,
          // Preserve fixed project values because a workstream-scoped report can
          // contain scoped hours and a proportionally scaled SOW value.
          projectSowValue: configuration.sowRate ?? loadedProject.sowRate,
          projectTotalHours: configuration.totalHours ?? loadedProject.totalProjectHours,
          discoveryHours: loadedProject.discoveryHours ?? configuration.discoveryHours,
          solutionArchitectureHours: loadedProject.solutionArchitectureHours ?? configuration.solutionArchitectureHours,
          developmentHours: loadedProject.developmentHours ?? configuration.developmentHours,
          uatHours: loadedProject.uatHours ?? configuration.uatHours,
          discoveryWeeks: loadedProject.discoveryWeeks ?? configuration.discoveryWeeks,
          solutionArchitectureWeeks: loadedProject.solutionArchitectureWeeks ?? configuration.solutionArchitectureWeeks,
          developmentWeeks: loadedProject.developmentWeeks ?? configuration.developmentWeeks,
          uatWeeks: loadedProject.uatWeeks ?? configuration.uatWeeks,
        });
        setWorkstreams(populateWorkstreamFinancials(loadedWorkstreams, loadedContributors));
        setContributors(loadedContributors);
      }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!signal?.aborted) setError(e instanceof Error ? e.message : "Unable to load workstream tracking");
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [includeGovernance, includeResearch, includeSalesCommission, includeShadow, projectId, token, workstreamId]);

  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);
  return { project, workstreams, contributors, loading, error, reload: load };
}

export function useProjectActivity(
  projectId: string,
  workstreamId: number | null,
  includeShadow: boolean,
  includeResearch: boolean,
  includeGovernance: boolean,
  includeSalesCommission: boolean,
  categories: ProjectActivityCategory[],
) {
  const [data, setData] = useState<ProjectActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const snapshotRef = useRef<string | null>(null);
  const token = Cookies.get("accessToken");

  const requestPage = useCallback(async (page: number, signal?: AbortSignal) => {
    if (!projectId) return null;
    const params = new URLSearchParams({
      includeShadow: String(includeShadow),
      includeResearch: String(includeResearch),
      includeGovernance: String(includeGovernance),
      includeSalesCommission: String(includeSalesCommission),
      page: String(page),
      size: "20",
    });
    if (workstreamId != null) params.set("workstreamId", String(workstreamId));
    if (page > 0 && snapshotRef.current) params.set("asOf", snapshotRef.current);
    categories.forEach((category) => params.append("categories", category));
    const response = await getApi<unknown>(
      `${API_PROJECT_WORKSTREAM_TRACKING}/projects/${projectId}/activity?${params}`,
      token,
      "json",
      signal,
    );
    return unwrap<ProjectActivityResponse>(response.data);
  }, [categories, includeGovernance, includeResearch, includeSalesCommission, includeShadow, projectId, token, workstreamId]);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!projectId) { setData(null); setError(null); return; }
    setLoading(true); setError(null);
    try {
      const next = await requestPage(0, signal);
      if (!signal?.aborted && next) { snapshotRef.current = next.asOf; setData(next); }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!signal?.aborted) setError(e instanceof Error ? e.message : "Unable to load project activity");
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [projectId, requestPage]);

  const loadMore = useCallback(async () => {
    if (!data?.hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await requestPage(data.page + 1);
      if (next) setData({ ...next, events: [...data.events, ...next.events] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to load more activity");
    } finally { setLoadingMore(false); }
  }, [data, loadingMore, requestPage]);

  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);
  return { data, loading, loadingMore, error, reload: load, loadMore };
}
